/* Copyright © 2024 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and */
/* matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, */
/* including without limitation, material generated from the software programs which are displayed on the screen such */
/* as icons, screen display looks, etc. */

import { Component, ViewChild, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from '../services/xda-strings.service';
import { XaccPaginationComponent } from './xacc-pagination.component';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';
import { XaccService } from './xacc.service';
import type {  
  MWReturningRepo,
  MWCloudItem,
  MWSelectedFilesInFolder,
  RepoItemList,
} from './xacc.service';

@Component({
  selector: 'xacc-browse',
  templateUrl: './xacc-browse.component.html',
  styleUrls: ['./xacc-browse.component.less'],
  standalone: false,
})
export class XaccBrowseComponent implements OnInit {
  connectorName: string;
  loading = false;
  loadingAll = false;
  isFirstPage = true;
  enableFilter = false;
  enableFileTypes: string;
  multiSelect: boolean;
  maxSelection: number;
  maxSelectionHit = false;
  browsePending = false;
  selectedFile: MWCloudItem; // for single selection
  currentSite: MWDisplayItem | null;
  currentLibrary: MWDisplayItem | null;
  currentFolder: MWDisplayItem;
  breadcrumb: MWDisplayItem[];
  unfilteredDisplayItems: MWDisplayItem[];
  filterPlaceholder: string;
  private _displayItems: MWDisplayItem[];
  public displayItems: MWDisplayItem[];
  public selectedFilesInFolders: MWSelectedFilesInFolder[] = [];
  returningRepo: MWReturningRepo;

  @Input() dir: MWCloudItem;
  @Input() returning = false;
  @Input() batchPageSize = 100;
  @ViewChild('paginator') paginator: XaccPaginationComponent;

  // pagination
  hidePaginationBar = true;
  page = 1;
  initialPageSize = 25;
  pageSize = 25;
  pageSizeList: any = [
    { size: 5 },
    { size: 10 },
    { size: 25 },
    { size: 50 },
    { size: 100 },
  ];
  collectionSize = 1;
  nextPageToken: string | null = null;
  hasBoundary = false; // set it to true when reach the end of contents
  private paginatedItems: MWDisplayItem[][] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private xaccService: XaccService,
    private modalService: NgbModal,
    private xdaStringsService: XdaStringsService,
  ) {
    this.filterPlaceholder =
      xdaStringsService.strings['SDE_ENTER_SEARCH_TERMS'];
  }

  async ngOnInit() {
    if (this.returning && this.returningRepo) {
      this.xaccService.setRepo(this.returningRepo);
      this.breadcrumb = this.returningRepo.sitePath;
      this.dir = this.returningRepo.dir;
      this.currentFolder = this.returningRepo.dir;
      this.selectedFilesInFolders = this.returningRepo.selectedFolders ?? [];
      this.connectorName = this.returningRepo.connector;

      if (this.xaccService.connector == 'GraphSharePoint') {
        // set current site and current lib using the last site and last lib in returningRepo
        for (let i = this.breadcrumb.length - 1; i !== 0; i--) {
          if (!this.currentSite && this.breadcrumb[i].type === 'site') {
            this.currentSite = this.breadcrumb[i];
          } else if (
            !this.currentLibrary &&
            this.breadcrumb[i].type === 'lib'
          ) {
            this.currentLibrary = this.breadcrumb[i];
          }
        }
      }
    } else {
      this.dir = { id: '', name: '', type: '' };
      this.setupHome();
    }

    if (
      this.xaccService.connector == 'GoogleDrive' &&
      this.enableFileTypes?.indexOf('.pdf') >= 0
    ) {
      this.enableFileTypes += ',.googledocs'; // allow .googledocs which download as pdf
    }

    // get data from repo
    await this.getDirectoryContents(this.currentFolder);
  }

  setupHome() {
    this.currentFolder = this.dir = this.getLanding(this.xaccService.connector);
    this.connectorName = this.currentFolder.name;
    this.currentSite = null;
    this.currentLibrary = null;
    this.breadcrumb = [];
    this.nextPageToken = null;
  }

  getLanding(connector: string): MWDisplayItem {
    let name = '';
    let type = 'land';

    switch (connector) {
      case 'GraphSharePoint':
        name = 'Microsoft® 365 SharePoint';
        break;
      case 'GraphOneDrive':
        name = 'Microsoft® OneDrive';
        type = 'dir';
        break;
      case 'GoogleDrive':
        name = 'Google Drive™';
        type = 'dir';
        break;
      case 'DocuShare':
        name = 'DocuShare® Go';
        type = 'dir';
        break;
      case 'Box':
        name = 'Box™';
        type = 'dir';
        break;
      case 'Dropbox':
        name = 'Dropbox™';
        type = 'dir';
        break;
      default:
        name = connector;
        type = 'dir';
        break;
    }
    return { id: '', type: type, glyph: '', name: name };
  }

  // home button handler
  async home(): Promise<void> {
    this.setupHome();
    this.loading = this.loadingAll = true;
    await this.getDirectoryContents(this.currentFolder);
  }

  getSelectedFilesInCurrentfolder(initialBrowse = false) {
    if (!this.displayItems) {
      return;
    }

    this.hidePaginationBar = true;
    let selectedFolder = null;
    if (this.selectedFilesInFolders && this.selectedFilesInFolders.length) {
      selectedFolder = this.selectedFilesInFolders.find(
        (x) => x.dir.id === this.dir?.id && x.site?.id === this.currentSite?.id,
      );
    }

    // if filter is on then include items in the unfiltered list
    const checkedItems = this.enableFilter
      ? this.unfilteredDisplayItems.filter((x) => x.checked === true)
      : this.displayItems.filter((x) => x.checked === true);
    if (checkedItems.length) {
      const selectedFiles = checkedItems.map(
        (item) => ({ name: item.name, id: item.id } as MWCloudItem),
      );
      if (selectedFolder) {
        selectedFolder.files = selectedFiles;
      } else {
        let dir: MWCloudItem | null = null,
          site: MWCloudItem = { id: '', name: '', type: '' };
        dir = this.dir
          ? { name: this.dir.name, id: this.dir.id, type: this.dir.type }
          : { name: '', id: '', type: '' };
        this.selectedFilesInFolders.push({
          site: site,
          dir: dir,
          files: selectedFiles,
        });
      }
    } else if (selectedFolder && !initialBrowse) {
      selectedFolder.files = [];
    }
  }

  getTotalSelectedFiles() {
    let totalSelectedFiles = 0;
    if (this.selectedFilesInFolders) {
      this.selectedFilesInFolders.forEach((f) => {
        if (f.files) {
          totalSelectedFiles += f.files.length;
        }
      });
    }
    return totalSelectedFiles;
  }

  back(): void {
    this.loading = this.loadingAll = true;
    //get selected file in the current folder before navigate away
    this.getSelectedFilesInCurrentfolder();
    setTimeout(async () => {
      await this.navBack();
    }, 0);
  }

  async navBack(): Promise<void> {
    // update currentFolder and breadcrumb
    this.dir = this.currentFolder =
      this.breadcrumb.pop() || [{ id: '', name: '', type: '' }][0];
    if (
      this.connectorName.includes('SharePoint') &&
      this.breadcrumb.length === 0
    ) {
      //reset
      this.currentSite = null;
      this.currentLibrary = null;
    }
    // Update display items
    this.nextPageToken = null;
    await this.getDirectoryContents(this.currentFolder);
  }

  navForward(item: MWDisplayItem): void {
    // Update breadcrumb and sitepath or dir
    // when we are not ignoring
    this.breadcrumb.push(this.currentFolder);

    if (item.type == 'site') {
      this.currentSite = item;
    } else if (item.type == 'lib') {
      this.currentLibrary = item;
    } else if (item.type == 'dir') {
      this.dir = item;
    }

    this.dir = this.currentFolder = item;
  }

  async toggleFilter() {
    // If filter is enabled, we are about to disable it, so set display items back to original
    if (this.enableFilter) {
      await this.resetFilter();
    } else {
      this.enableFilter = true;
    }
  }

  disableFilter() {
    this.resetFilter();
  }

  async resetFilter() {
    if (this.enableFilter) {
      this.paginate(this.unfilteredDisplayItems);
      this.displayItems = this.paginatedItems[this.page - 1];
      this.paginator.rebuildPageList(
        this.unfilteredDisplayItems.length,
        this.page,
        this.hasBoundary,
      );
    }

    this.enableFilter = false;
  }

  async filterDisplayItems(filterText: string) {
    if (!filterText) {
      return;
    }

    if (this.hasBoundary) {
      // client side filtering
      const filteredItems = this.unfilteredDisplayItems.filter((item) =>
        item.name.toLowerCase().includes(filterText.toLowerCase()),
      );
      this.isFirstPage = true;
      this.paginate(filteredItems);
      this.displayItems = this.paginatedItems[this.page - 1];
      this.paginator.rebuildPageList(
        filteredItems.length,
        this.page,
        this.hasBoundary,
      );
    } else {
      // server side filtering
      this.hidePaginationBar = true;
      await this.getDirectoryContents(this.currentFolder, filterText);
    }

    if (!this.displayItems) {
      this.displayItems = [];
    }
  }

  async filterHandleEnterKeyPress(event: Event) {
    event.preventDefault();
    const target = event.target as HTMLInputElement;
    await this.filterDisplayItems(target.value);
  }

  async browse(
    item: MWDisplayItem,
    initialBrowse = false,
  ): Promise<boolean> {
    if (!item) {
      return false;
    }

    // reset nextPageToken
    this.nextPageToken = null;
    //get selected file in the current folder before navigate away
    this.getSelectedFilesInCurrentfolder(initialBrowse);
    this.maxSelectionHit = this.getTotalSelectedFiles() === this.maxSelection;

    if (item.type === 'file') {
      if (this.multiSelect) {
        if (item.selected) {
          item.selected = false;
        } else {
          item.selected = true;
        }
      } else {
        this.selectedFile = { name: item.name, id: item.id, type: item.type };
      }

      return true;
    }

    this.loading = this.loadingAll = true;

    // Disable Filter if it is enabled
    this.resetFilter();

    // Navigate forward
    if (item.id) {
      this.navForward(item);
    }

    await this.getDirectoryContents(item);
    return true;
  }

  /***
   *  This function is called only in Multiple selection mode
   */
  checkSelectedFiles() {
    if (
      Array.isArray(this.selectedFilesInFolders) &&
      this.selectedFilesInFolders.length
    ) {
      if (this.dir.name === '/') {
        this.dir.name = '';
      }
      const curFolder = this.selectedFilesInFolders.find(
        (file) =>
          file.dir.name === this.dir.name &&
          file.site?.id === this.currentSite?.id,
      );
      if (curFolder) {
        this.displayItems.forEach((item) => {
          if (curFolder.files.find((file) => file.name === item.name)) {
            item.checked = true;
          }
        });
      }
    }
  }

  async getContents(
    item: MWDisplayItem,
    query = '',
    pageSize = 0,
  ): Promise<RepoItemList | null> {
    pageSize = pageSize !== 0 ? pageSize : this.pageSize;
    const pSize =
      pageSize === -1 || pageSize > this.batchPageSize
        ? pageSize
        : this.batchPageSize;

    let folderId = '';
    if (this.connectorName.includes('SharePoint')) {
      if (item.type === 'site') {
        // cached the site
        this.currentSite = item;
        this.currentLibrary = null;
      } else if (item.type === 'lib') {
        // cached library
        this.currentLibrary = item;
      } else {
        folderId = item.id;
      }
    } else {
      folderId = item.id;
    }

    // set pageToken to empty for query
    const pageToken = query === '' ? this.nextPageToken : '';
    return await this.xaccService.browseById(
      this.currentSite?.id || '',
      this.currentLibrary?.id || '',
      folderId,
      query,
      pSize,
      pageToken || '',
    );
  }

  async getDirectoryContents(
    folder: MWDisplayItem,
    query = '',
    pageSize = 0,
    isFirstPage = true,
  ) {
    const files: MWDisplayItem[] = [];
    const dirs: MWDisplayItem[] = [];
    this.loading = true;
    this.isFirstPage = isFirstPage;
    const isFilterQuery = query === '' ? false : true;
    if (isFirstPage && !query) {
      this.unfilteredDisplayItems = [];
    }

    try {
      const repoItemList = await this.getContents(folder, query, pageSize);

      // set hasBoundary when this is NOT a server side filtering
      if (repoItemList && query === '') {
        this.nextPageToken = repoItemList.nextPageToken || null;
        this.hasBoundary = true;
        if (this.nextPageToken) {
          this.hasBoundary = false;
        }
      }

      // process returned items for display
      repoItemList?.items.forEach((item) => {
        if (item.mimeType.includes('folder')) {
          dirs.push({
            id: item.id,
            type: 'dir',
            glyph: 'xgl-folder',
            name: item.name,
            readOnly: false,
          });
        }
        // Added / to stop vnd.google-apps.site files
        else if (item.mimeType.includes('/site')) {
          let siteGlyph = 'xgl-network_square';
          if (item.id === 'teamSites') {
            siteGlyph = 'xgl-group';
          } else if (item.id === 'followedSites') {
            siteGlyph = 'xgl-favorites';
          }
          dirs.push({
            id: item.id,
            type: 'site',
            glyph: siteGlyph,
            name: item.name,
            readOnly: false,
          });
        } else if (item.mimeType.includes('Library')) {
          dirs.push({
            id: item.id,
            type: 'lib',
            glyph: 'xgl-accounting',
            name: item.name,
          });
        } else {
          files.push({
            id: item.id,
            type: 'file',
            glyph: 'xgl-file',
            name: item.name,
          });
        }
      });

      files.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      dirs.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      this.paginate(dirs.concat(files), isFilterQuery);
      const updatedTotal: MWDisplayItem[] = this.paginatedItems.flat();

      // next button click for server browsing, update unfilteredDisplayItems[]
      if (
        !isFilterQuery &&
        updatedTotal.length > this.unfilteredDisplayItems.length
      ) {
        this.unfilteredDisplayItems = updatedTotal;
      }

      this.displayItems =
        this.paginatedItems.length === 0
          ? []
          : this.paginatedItems[this.page - 1];
      const totalItems = isFilterQuery
        ? updatedTotal.length
        : this.unfilteredDisplayItems.length;

      // server side filtering (query !== "") always returns all matched results, that is, always has a boundary
      const hasBoundary = this.hasBoundary || isFilterQuery;
      this.paginator.rebuildPageList(totalItems, this.page, hasBoundary);
    } catch (e) {
      console.log(e);
      if ((e as { status: number }).status !== undefined) {
        const error = e as { status: number };
        if (error.status) {
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title =
            error.status == 400 || error.status == 404
              ? 'SDE_DESTINATION_NOT_FOUND'
              : 'SDE_SYSTEM_ENCOUNTERED_ERROR1';
          modalRef.result.then(() => {
            this.home(); // reset to home
          });
        }
      }
    } finally {
      this.loading = this.loadingAll = false;
    }
  }

  paginate(items: any[], isFilerQuery = false) {
    if (this.isFirstPage) {
      this.paginatedItems = [];
      this.page = 1;
    }

    const currentPageCount = this.paginatedItems.length;
    let index = 0;

    // sometimes repo returns items less than specified page size
    if (
      currentPageCount > 0 &&
      this.paginatedItems[currentPageCount - 1].length < this.pageSize
    ) {
      //fill up the current page
      const toBefilled =
        this.pageSize - this.paginatedItems[currentPageCount - 1].length;
      for (let r = 0; r < Math.min(toBefilled, items.length); r++) {
        this.paginatedItems[currentPageCount - 1].push(items[index++]);
      }
    }

    for (let i = 0; index < items.length; i++) {
      const remaining = items.length - index;
      this.paginatedItems[i + currentPageCount] = [];
      for (let j = 0; j < Math.min(this.pageSize, remaining); j++) {
        this.paginatedItems[i + currentPageCount][j] = items[index++];
      }
    }

    // the last button was clicked, set page to the last page
    if (this.page === -1 || this.page > this.paginatedItems.length) {
      this.page = this.paginatedItems.length;
    }

    if (this.isFirstPage) {
      this.hidePaginationBar =
        this.paginatedItems.length < 2 && (this.hasBoundary || isFilerQuery);
    }
  }

  repaginate() {
    const items: MWDisplayItem[] = this.paginatedItems.flat();
    this.paginatedItems = [];
    this.paginate(items);
  }

  pageChanged(selectedPage: any) {
    if (selectedPage === -1) {
      // pagesize = -1 to get all rest items
      this.getDirectoryContents(this.currentFolder, '', -1, false); // isFirstPage: false
    } else if (selectedPage <= this.paginatedItems.length) {
      // set current page to be displayed
      this.displayItems = this.paginatedItems[this.page - 1];
    } else {
      // get more data...
      this.getDirectoryContents(this.currentFolder, '', this.pageSize, false); // isFirstPage: false
    }
  }

  pageSizeChange(selectedPageSize: any) {
    if (this.pageSize !== selectedPageSize) {
      this.pageSize = selectedPageSize;
      this.repaginate();
      this.page = 1;
      this.displayItems = this.paginatedItems[this.page - 1];
      // we want to keep the pagination bar after page size changed
      this.hidePaginationBar = false;
    }
  }

  // Used to detrmine if a folder or file has valid characters (middleware api error's if you try and give it back any files with these characters)
  isValidItem(item: string): boolean {
    const invalidCharacters = new RegExp('[<>\\/""*:]');
    return !invalidCharacters.test(item);
  }

  isFileTypeEnabled(fileName = '') {
    if (!this.enableFileTypes) {
      return false;
    }

    const index = fileName.lastIndexOf('.');
    if (index !== -1) {
      const ext = fileName.substr(index).toLowerCase();
      return this.enableFileTypes.includes(ext);
    }

    return false;
  }

  isItemEnabled(item: MWDisplayItem) {
    if (item.type == 'file') {
      return this.isFileTypeEnabled(item.name);
    } else if (item.type == 'dir' && !this.enableFileTypes) {
      return !item.readOnly;
    }
    return true;
  }

  // return the selected items.
  selectDirectoryOrFiles() {
    this.activeModal.close({
      connector: this.xaccService.connector,
      region: this.xaccService.region,
      sitePath: this.breadcrumb,
      dir: this.dir,
      files: this.multiSelect ? this.selectedFilesInFolders : this.selectedFile,
      access: this.xaccService.connectorToken,
      refresh: this.xaccService.connectorRefreshToken,
    });
  }

  cancel() {
    this.activeModal.dismiss({
      connector: this.xaccService.connector,
      region: this.xaccService.region,
      sitePath: null, // indicate Cancel with null values
      dir: null,
      files: null,
      access: this.xaccService.connectorToken,
      refresh: this.xaccService.connectorRefreshToken,
    });
  }
}

export interface MWDisplayItem extends MWCloudItem {
  glyph?: string;
  ignore?: boolean;
  checked?: boolean;
  readOnly?: boolean;
  selected?: boolean;
}
